import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { posService } from '../../../lib/posService';

const CategoryManager = ({ categories, gymId, onCategoriesChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: '0'
  });
  const [saving, setSaving] = useState(false);

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category?.name || '',
      description: category?.description || '',
      display_order: category?.display_order?.toString() || '0'
    });
    setIsAdding(true);
  };

  const handleSave = async () => {
    if (!formData?.name) {
      alert('Please enter a category name');
      return;
    }

    setSaving(true);
    const categoryData = {
      ...formData,
      display_order: parseInt(formData?.display_order) || 0
    };

    const { error } = editingCategory
      ? await posService?.updateCategory(editingCategory?.id, categoryData)
      : await posService?.createCategory(gymId, categoryData);

    if (error) {
      alert(error?.message || 'Failed to save category');
    } else {
      setIsAdding(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', display_order: '0' });
      onCategoriesChange();
    }
    setSaving(false);
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category? Products in this category will be uncategorized.')) return;

    const { error } = await posService?.deleteCategory(categoryId);
    if (error) {
      alert(error?.message || 'Failed to delete category');
    } else {
      onCategoriesChange();
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md border border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Product Categories</h2>
          <Button
            onClick={() => {
              setIsAdding(true);
              setEditingCategory(null);
            }}
            iconName="Plus"
            iconPosition="left"
            size="sm"
          >
            Add Category
          </Button>
        </div>
      </div>

      {isAdding && (
        <div className="p-6 border-b border-border bg-muted/20">
          <h3 className="font-semibold text-foreground mb-4">
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Category Name"
              type="text"
              placeholder="Enter category name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              required
            />
            <Input
              label="Display Order"
              type="number"
              placeholder="0"
              value={formData?.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: e?.target?.value })}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Enter category description"
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsAdding(false);
                setEditingCategory(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              iconName="Save"
              iconPosition="left"
            >
              {saving ? 'Saving...' : 'Save Category'}
            </Button>
          </div>
        </div>
      )}

      <div className="p-4">
        {categories?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FolderOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">No categories yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories?.map(category => (
              <div key={category?.id} className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">{category?.name}</h3>
                    {category?.description && (
                      <p className="text-xs text-muted-foreground">{category?.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-1 rounded hover:bg-muted transition-colors"
                    >
                      <Icon name="Edit" size={16} color="var(--color-primary)" />
                    </button>
                    <button
                      onClick={() => handleDelete(category?.id)}
                      className="p-1 rounded hover:bg-error/10 transition-colors"
                    >
                      <Icon name="Trash2" size={16} color="var(--color-error)" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Package" size={12} />
                  <span>Order: {category?.display_order}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;